import React, { useMemo, useRef } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import globalWindow from '@shared/core/globals';
import { createContext } from '@shared/utils/createContext';
import type { ApplicationContext } from './styleApplicator.types';
import type { ThemeArtRecorder } from './themeArtRecorder.types';
import type { ThemeOutput } from './themeOutput.types';

/** themeId → the theme's published style JSON, as resolved during SSR. */
export type InitialThemeStyles = Readonly<Record<string, ThemeOutput>>;

type ThemeStylesContextValue = Readonly<{
  getThemeStyles: (themeId: string, context: ApplicationContext) => ThemeOutput | undefined;
  markPainted: (context: ApplicationContext) => void;
}>;

const noop = () => {};

// strict: false — most surfaces never mount this provider, and its absence simply means
// "nothing was seeded, fetch on the client" rather than a misconfiguration.
const [ThemeStylesContextProvider, useThemeStylesContext] = createContext<ThemeStylesContextValue>({
  name: 'ThemeStyles',
  strict: false,
});

/**
 * Makes the theme art available during render so sprites can be server-rendered (PRNT-4898).
 *
 * Mirrors the Amplitude-variant seeding contract, with the server side resolved lazily: on the
 * server, `themeArtRecorder` answers each canvas as it renders and records what was read; the
 * middleware inlines that record as `window.__INITIAL_THEME_STYLES__`, which the client reads back
 * here. Both sides therefore hand `useThemeCanvas` the identical ThemeOutput and run the identical
 * derivation over it, so the server markup and the first client render agree — the raw JSON is
 * seeded rather than the derived sprites precisely so the derivation cannot drift between them.
 *
 * `initialThemeStyles` overrides the window seed (tests, stories). With neither a recorder nor a
 * seed this is inert and `useThemeCanvas` falls back to fetching on the client, exactly as before.
 *
 * Seeds ONLY. It is mounted in Setup — every app, every page — so it deliberately reads no
 * feature flag: a remote-flag read records an Amplitude exposure, and this provider's position
 * would attribute one to every admin, registry and card page view. The applicator decision lives
 * in `BundledApplicatorProvider`, mounted only where theme art can render, and useThemeCanvas only
 * asks for styles when that decision is on — so with the flag off the recorder is never called.
 */
export const ThemeStylesProvider: React.FC<{ initialThemeStyles?: InitialThemeStyles; themeArtRecorder?: ThemeArtRecorder }> = ({
  initialThemeStyles,
  themeArtRecorder,
  children,
}) => {
  // Kept free of the fetch layer on purpose: this provider is mounted in Setup, which lives in
  // the entry bundle, so anything it imports ships to every app including admin. Consumers of
  // the seeded styles (useThemeCanvas) live in the guest layout chunks. The recorder is a TYPE
  // import; its implementation (and the bundled theme data) stays server-side.
  const value = useMemo((): ThemeStylesContextValue => {
    if (themeArtRecorder) {
      return { getThemeStyles: themeArtRecorder.resolve, markPainted: themeArtRecorder.markPainted };
    }
    const styles = initialThemeStyles ?? (globalWindow.__INITIAL_THEME_STYLES__ as InitialThemeStyles | undefined);
    return { getThemeStyles: (themeId: string) => styles?.[themeId], markPainted: noop };
  }, [initialThemeStyles, themeArtRecorder]);

  return <ThemeStylesContextProvider value={value}>{children}</ThemeStylesContextProvider>;
};

type BundledApplicatorContextValue = Readonly<{
  /** The page's single, latched answer to "is the bundled applicator on?". */
  isBundledApplicatorEnabled: boolean;
}>;

// strict: false — consumers outside a provider (the admin ecard editor) fall back to a
// per-instance latch, see useBundledApplicatorEnabled.
const [BundledApplicatorContextProvider, useBundledApplicatorContext] = createContext<BundledApplicatorContextValue>({
  name: 'BundledApplicator',
  strict: false,
});

/**
 * Latches the `websiteBundleStyleApplicator` decision for one page (PRNT-4898). Mount it at the
 * root of a surface that renders theme art — the guest site, ecard and save-the-date guest views,
 * the guest registry — above both the layout writers and `StyleApplicatorScript`.
 *
 * Latched on first render so the whole page commits to one applicator once. Reading the flag live
 * in each consumer let them disagree: useStyleApplicator could latch `legacy` while the flag was
 * unresolved, then a later resolution would switch useThemeCanvas on and paint React art over the
 * legacy sprites (PRNT-4856 double-paint).
 *
 * Mounted per surface rather than in Setup because reading a remote flag records an Amplitude
 * exposure: at the app root that meant an exposure for this guest-site flag on every admin,
 * registry and card page — noise for a kill switch, and fatal to the flag as an experiment.
 */
export const BundledApplicatorProvider: React.FC = ({ children }) => {
  const [isBundledApplicatorEnabled] = useIsFeatureEnabled('websiteBundleStyleApplicator');
  const latchedEnabled = useRef(isBundledApplicatorEnabled).current;
  const value = useMemo((): BundledApplicatorContextValue => ({ isBundledApplicatorEnabled: latchedEnabled }), [latchedEnabled]);

  return <BundledApplicatorContextProvider value={value}>{children}</BundledApplicatorContextProvider>;
};

/**
 * The page's latched applicator decision. Falls back to a per-instance latch on surfaces that
 * don't mount the provider (the admin ecard editor renders theme art without one), so the value
 * can still never flip mid-mount. The fallback does read the flag, so call this only from code
 * that renders theme art — anything else should use `useBundledApplicatorEnabledIfProvided`.
 */
export const useBundledApplicatorEnabled = (): boolean => {
  const context = useBundledApplicatorContext();
  const [isEnabled] = useIsFeatureEnabled('websiteBundleStyleApplicator');
  const fallbackLatched = useRef(isEnabled).current;
  return context ? context.isBundledApplicatorEnabled : fallbackLatched;
};

/**
 * The latched decision where a `BundledApplicatorProvider` took one, and `false` — without
 * consulting the flag — everywhere else. For code that must stay on its legacy behaviour unless a
 * theme-art surface has explicitly opted in: `StyleApplicatorScript` is mounted in AdminHead on
 * every admin page, and reading the flag there would record an exposure per admin page view.
 */
export const useBundledApplicatorEnabledIfProvided = (): boolean => useBundledApplicatorContext()?.isBundledApplicatorEnabled ?? false;

/**
 * Returns the SSR-seeded styles for a theme, or undefined when nothing was seeded. On the server
 * this is also what records the theme and `canvas` for the client seed, so pass the canvas the
 * caller is about to read — a canvas read without being recorded would be missing from the seed
 * and break hydration.
 */
export const useSeededThemeStyles = (themeId: string | undefined | null, canvas: ApplicationContext): ThemeOutput | undefined => {
  const context = useThemeStylesContext();
  return themeId ? context?.getThemeStyles(themeId, canvas) : undefined;
};

/**
 * Returns a callback that records "sprites for this canvas were rendered" — the signal the server
 * uses to decide whether the page gets art preloads. A no-op on the client and without a provider.
 */
export const useMarkThemeArtPainted = (): ((context: ApplicationContext) => void) => useThemeStylesContext()?.markPainted ?? noop;
